import React, {CSSProperties, FormEvent, RefObject, useState} from 'react';
import classNames from "classnames";
import intl from "react-intl-universal";


import {EyeIcon} from "../../icons/EyeIcon";
import {EyeOffIcon} from "../../icons/EyeOffIcon";
import {DangerIcon} from "../../icons/DangerIcon";
import {Tooltip} from "../../Tooltip"


import './MaterialUiInput.css';

/*
* Container
* Label
* Input
*
       Container
(0,0)┌── Label ─────────────────────────────────┐
     │   ^ inner_padding                        │
     │  +---------------------------------+     │
     │  │  Input   input_line_height      │     │
     │  +---------------------------------+     │
     └──────────────────────────────────────────┘

 --vertical-center-shift - is the shift needed to center Label from (0,0) to the middle od the Container, where Input is initially centered.
 the top attribute relative to Container to achieve that can be  calculated with  (containerFullHeight / 2 - Label_height / 2)
 containerFullHeight = input_line_height + 2*inner_padding
 *
 * MaterialUiInput__label default transform positions label to the center fo the top border
 * MaterialUiInput--empty transforms position to the middle of the Container with the var(--vertical-center-shift)
* */


export interface MaterialUiInputProps {
    label: string;
    name: string;
    type?: string;
    theme?: 'dark' | 'light';
    value?: string;
    trimSpace?: boolean;
    showPassword?: boolean;
    setShowPassword?: (newValue: boolean) => void;
    onChange?: (newValue: string) => void;
    className?: string;
    style?: CSSProperties;
    isRequired?: boolean;
    fullWidth?: boolean;
    error?: string | React.ReactElement;
    autoComplete?: string;
    innerRef?: RefObject<any>;
    isGrayBackground?: boolean;
    emptyFieldError?: string;
}

export const MaterialUiInput = (
    {
        label,
        name,
        theme = 'light',
        className,
        style,
        type = 'text',
        fullWidth = false,
        trimSpace = false,
        value,
        showPassword,
        setShowPassword,
        isRequired,
        onChange,
        error,
        autoComplete = name,
        isGrayBackground,
        innerRef,
        emptyFieldError = intl.get('input-error.isRequired').d('Field is required.')
    }: MaterialUiInputProps) => {
    const [isFocused, setIsFocused] = useState(false);
    const [requiredError, setIsRequiredError] = useState('');
    const [uncontrolledValue, setUncontrolledValue] = useState('');

    const onInvalid = (event: FormEvent<HTMLInputElement>) => {
        if ((event.target as HTMLInputElement).validity.valueMissing) {
            event.preventDefault();
            event.stopPropagation();
            setIsRequiredError(emptyFieldError);
        }
    }

    const onInput = (event: FormEvent<HTMLInputElement>) => {
        if (requiredError && !(event.target as HTMLInputElement).validity.valueMissing) {
            setIsRequiredError('');
        }
    }

    let actualValue = value === undefined ? uncontrolledValue : value;
    let actualError = error ? error : requiredError;
    let isLabelRaised = !!actualValue || isFocused;

    return (
        <div className={classNames({
            'MaterialUiInput': true,
            'theme-dark': theme === 'dark',
            'MaterialUiInput--empty': !actualValue,
            'MaterialUiInput--focused': isFocused,
            'MaterialUiInput--full': fullWidth,
            'MaterialUiInput--error': !!actualError,
            [className!]: !!className,
        })}>
            <input
                key={name}
                className='MaterialUiInput__input body-large'
                id={name}
                name={name}
                value={actualValue}
                type={showPassword ? 'text' : type}
                onChange={
                    onChange
                        ? (e) => onChange(trimSpace ? e.target.value.trim() : e.target.value)
                        : (e) => setUncontrolledValue(trimSpace ? e.target.value.trim() : e.target.value)
                }
                required={isRequired}
                onFocus={() => {
                    setIsFocused(true);
                }}
                onBlur={() => {
                    setIsFocused(false);
                }}
                autoComplete={autoComplete}
                ref={innerRef}
                onInvalid={onInvalid}
                onInput={onInput}
                style={style}
            />
            <label className={classNames({
                'MaterialUiInput__label': true,
                'body-large': !isLabelRaised,
                'label-large MaterialUiInput__label--raised': isLabelRaised,
                'MaterialUiInput--grayBackground': isGrayBackground,
            })} htmlFor={name}>{label}</label>
            {
                type === 'password'
                    ? (
                        showPassword
                            ?
                            <Tooltip title={intl.get('tooltip.profile.hidePassword').d("Hide Password")} isInBox={false}>
                                <EyeOffIcon size={20} className={'MaterialUiInput__input-show-pwd'}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                setShowPassword!(false)
                                            }}/>
                            </Tooltip>
                            :
                            <Tooltip title={intl.get('tooltip.profile.showPassword').d("Show Password")} isInBox={false}>
                                <EyeIcon size={20} className={'MaterialUiInput__input-show-pwd'}
                                         onClick={(event) => {
                                             event.stopPropagation();
                                             setShowPassword!(true)
                                         }}/>
                            </Tooltip>
                    )
                    : null}
            {actualError
                ? (
                    <div className='MaterialUiInput__input-error'>
                        <DangerIcon
                            className='MaterialUiInput__input-error-icon'
                            size={16}
                        />
                        <span className='MaterialUiInput__input-error-text label-large'>{actualError}</span>
                    </div>
                )
                : null}
        </div>
    )
}
